import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import './App.css';
import DashboardPage from './pages/DashboardPage';
import DashboardLogsPage from './pages/DashboardLogsPage';
import DeviceListPage from './pages/DeviceListPage';
import GreenScorePage from './pages/GreenScorePage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import MainDashboardPage from './pages/MainDashboardPage';
import MobileDashboardPage from './pages/MobileDashboardPage';
import MobileDashboardLogsPage from './pages/MobileDashboardLogsPage';
import MobileLoginPage from './pages/MobileLoginPage';
import MobileMainDashboardPage from './pages/MobileMainDashboardPage';
import MobilePage from './pages/MobilePage';
import RegisterPage from './pages/RegisterPage';
import SuperAdminPage from './pages/SuperAdminPage';
import TechConfigPage from './pages/TechConfigPage';
import { getLoginSession, getLoginUserRole } from './services/authApi';

const THEME_STORAGE_KEY = 'cnergi-theme';

function getInitialTheme() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

  if (storedTheme === 'dark' || storedTheme === 'light') {
    return storedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function HomeRoute({ isDarkMode, onToggleTheme, onSelectTheme }) {
  const loginSession = getLoginSession();
  const role = getLoginUserRole(loginSession);

  if (role) {
    if (role === 'super admin') {
      return <SuperAdminPage isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
    }

    if (role === 'tech' || role === 'super tech') {
      return <TechConfigPage isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
    }

    return <MainDashboardPage isDarkMode={isDarkMode} onSelectTheme={onSelectTheme} />;
  }

  return <LandingPage isDarkMode={isDarkMode} onToggleTheme={onToggleTheme} />;
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);
  const isDarkMode = theme === 'dark';

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((currentTheme) => currentTheme === 'dark' ? 'light' : 'dark');
  };

  const selectTheme = (nextTheme) => {
    setTheme(nextTheme);
  };

  return (
    <div className="app" data-theme={theme}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={(
              <HomeRoute
                isDarkMode={isDarkMode}
                onToggleTheme={toggleTheme}
                onSelectTheme={selectTheme}
              />
            )}
          />
          <Route
            path="/login"
            element={<LoginPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/register"
            element={<RegisterPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/tech"
            element={<TechConfigPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/super-admin"
            element={<SuperAdminPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile"
            element={<MobilePage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/login"
            element={<MobileLoginPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/maindash"
            element={<MobileMainDashboardPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/dashboard"
            element={<MobileDashboardPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/dashboard/logs"
            element={<MobileDashboardLogsPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/dashboard/:deviceId"
            element={<MobileDashboardPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/mobile/dashboard/:deviceId/logs"
            element={<MobileDashboardLogsPage isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
          />
          <Route
            path="/dashboard"
            element={<DashboardPage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route
            path="/dashboard/:deviceId"
            element={<DashboardPage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route
            path="/dashboard/:deviceId/logs"
            element={<DashboardLogsPage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route
            path="/maindash"
            element={<MainDashboardPage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route
            path="/maindash/devices"
            element={<DeviceListPage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route
            path="/maindash/greenscore"
            element={<GreenScorePage isDarkMode={isDarkMode} onSelectTheme={selectTheme} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
